// src/utils/flightCalculations.js
//
// Time, timezone, twilight and distance helpers shared by the flight form
// (and anything else that needs them). Pure functions where possible; the
// airport lookups hit /api/airports/:code and are cached per page load.

import axios from 'axios';

export const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// ─── Time parsing ────────────────────────────────────────────────────────────

// Convert time string (HH:MM or HHMM) to minutes
export const convertTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;

  let cleanTime = timeStr.toString().replace(/[^0-9]/g, '');

  if (cleanTime.length === 1 || cleanTime.length === 2) {
    // Just hours (e.g., "8" or "14")
    cleanTime = cleanTime.padEnd(4, '0');
  } else if (cleanTime.length === 3) {
    // Hours and partial minutes (e.g., "830")
    cleanTime = '0' + cleanTime;
  } else if (cleanTime.length > 4) {
    cleanTime = cleanTime.substring(0, 4);
  }

  if (cleanTime.length !== 4) return 0;

  const hours = parseInt(cleanTime.substring(0, 2), 10);
  const minutes = parseInt(cleanTime.substring(2), 10);

  if (hours > 23 || minutes > 59) return 0;

  return hours * 60 + minutes;
};

// Combine a YYYY-MM-DD date and a 4-digit time into an ISO datetime string
export const convertTimeToDatetime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;

  const cleanTime = timeStr.toString().replace(/[^0-9]/g, '');
  if (cleanTime.length < 3) return null;

  const paddedTime = cleanTime.padStart(4, '0');
  const hours = paddedTime.slice(0, 2);
  const minutes = paddedTime.slice(2, 4);

  if (parseInt(hours) > 23 || parseInt(minutes) > 59) return null;

  return `${dateStr}T${hours}:${minutes}:00`;
};

// Actual block minus scheduled block, in decimal hours (local times, no tz)
export const calculateBlockDiff = (scheduledOut, scheduledIn, actualOut, actualIn) => {
  if (!scheduledOut || !scheduledIn || !actualOut || !actualIn) {
    return null;
  }

  const scheduledOutMinutes = convertTimeToMinutes(scheduledOut);
  const scheduledInMinutes = convertTimeToMinutes(scheduledIn);
  const actualOutMinutes = convertTimeToMinutes(actualOut);
  const actualInMinutes = convertTimeToMinutes(actualIn);

  if (scheduledOutMinutes === 0 || scheduledInMinutes === 0 ||
      actualOutMinutes === 0 || actualInMinutes === 0) {
    return null;
  }

  let scheduledBlockMinutes = scheduledInMinutes - scheduledOutMinutes;
  if (scheduledBlockMinutes < 0) scheduledBlockMinutes += 24 * 60; // overnight

  let actualBlockMinutes = actualInMinutes - actualOutMinutes;
  if (actualBlockMinutes < 0) actualBlockMinutes += 24 * 60; // overnight

  return Math.round(((actualBlockMinutes - scheduledBlockMinutes) / 60) * 10) / 10;
};

// Duration between two local times at (possibly) different UTC offsets.
// Returns { totalMinutes, utcOutMinutes, utcInMinutes } normalised to 0-1440.
export const durationAcrossTimezones = (outMinutes, inMinutes, departureOffset, arrivalOffset) => {
  let utcOutMinutes = outMinutes - (departureOffset * 60);
  let utcInMinutes = inMinutes - (arrivalOffset * 60);

  while (utcOutMinutes < 0) utcOutMinutes += 24 * 60;
  while (utcOutMinutes >= 24 * 60) utcOutMinutes -= 24 * 60;
  while (utcInMinutes < 0) utcInMinutes += 24 * 60;
  while (utcInMinutes >= 24 * 60) utcInMinutes -= 24 * 60;

  let totalMinutes = utcInMinutes - utcOutMinutes;
  if (totalMinutes < 0) totalMinutes += 24 * 60; // crossing midnight UTC

  return { totalMinutes, utcOutMinutes, utcInMinutes };
};

export const minutesToHours = (minutes) => Math.round((minutes / 60) * 10) / 10;

// ─── Airports & timezones ────────────────────────────────────────────────────

// Airport records don't change while the form is open, but the form looks the
// same airport up several times per keystroke — cache the in-flight promise.
const airportCache = new Map();

export const fetchAirport = (code) => {
  const key = code.toUpperCase();
  if (!airportCache.has(key)) {
    const request = axios.get(`/api/airports/${key}`, getAuthHeaders())
      .then(res => res.data)
      .catch(err => {
        airportCache.delete(key); // don't cache failures
        throw err;
      });
    airportCache.set(key, request);
  }
  return airportCache.get(key);
};

// Helper function to get the Nth Sunday of a month
const getNthSundayOfMonth = (year, month, n) => {
  const firstDay = new Date(year, month, 1);
  const firstSunday = new Date(firstDay);
  firstSunday.setDate(1 + (7 - firstDay.getDay()) % 7);

  const nthSunday = new Date(firstSunday);
  nthSunday.setDate(firstSunday.getDate() + (n - 1) * 7);

  return nthSunday;
};

// UTC offset in hours for an IANA timezone on a given date (DST-aware)
export const getTimezoneOffset = (timezoneId, date) => {
  try {
    if (!timezoneId) {
      console.warn('No timezone ID provided');
      return 0;
    }

    // Method 1: Use Intl.DateTimeFormat (most reliable)
    try {
      const utcDate = new Date(`${date}T12:00:00.000Z`);
      const fmt = new Intl.DateTimeFormat('en', {
        timeZone: timezoneId,
        timeZoneName: 'longOffset'
      });

      const offsetPart = fmt.formatToParts(utcDate).find(part => part.type === 'timeZoneName');

      if (offsetPart && offsetPart.value) {
        const match = offsetPart.value.match(/GMT([+-])(\d{1,2}):?(\d{2})?/);
        if (match) {
          const sign = match[1] === '+' ? 1 : -1;
          const hours = parseInt(match[2]);
          const minutes = parseInt(match[3] || '0');
          return sign * (hours + minutes / 60);
        }
      }
    } catch (intlError) {
      console.log('Intl method failed, trying alternative method:', intlError);
    }

    // Method 2: Compare against toLocaleString in the target timezone
    try {
      const utcDate = new Date(`${date}T12:00:00.000Z`);
      const targetDate = new Date(utcDate.toLocaleString('en-US', { timeZone: timezoneId }));
      return (utcDate.getTime() - targetDate.getTime()) / (1000 * 60 * 60);
    } catch (fallbackError) {
      console.log('Alternative method also failed:', fallbackError);
    }

    // Method 3: Hard-coded US timezones with DST rules
    const baseOffsets = {
      'America/New_York': -5,
      'America/Chicago': -6,
      'America/Denver': -7,
      'America/Los_Angeles': -8,
      'America/Anchorage': -9,
      'Pacific/Honolulu': -10    // no DST
    };

    if (baseOffsets[timezoneId]) {
      const year = new Date(date).getFullYear();
      const testDate = new Date(date);

      // DST in US: Second Sunday in March to First Sunday in November
      const dstStart = getNthSundayOfMonth(year, 2, 2);
      const dstEnd = getNthSundayOfMonth(year, 10, 1);
      const isDST = testDate >= dstStart && testDate < dstEnd;

      return baseOffsets[timezoneId] + (isDST && timezoneId !== 'Pacific/Honolulu' ? 1 : 0);
    }

    console.warn(`No offset calculation method worked for ${timezoneId}`);
    return 0;
  } catch (error) {
    console.error('Error calculating timezone offset:', error, 'for timezone:', timezoneId);
    return 0;
  }
};

export const getAirportTimezone = async (airportCode, flightDate = null) => {
  if (!airportCode) return 0;

  try {
    const airport = await fetchAirport(airportCode);
    if (!airport) return 0;

    if (airport.time_zone) {
      const date = flightDate || new Date().toISOString().split('T')[0];
      return getTimezoneOffset(airport.time_zone, date);
    } else if (airport.utc_offset_hours) {
      console.warn(`No timezone found for ${airportCode}, using static offset: ${airport.utc_offset_hours}`);
      return parseFloat(airport.utc_offset_hours);
    }

    return 0;
  } catch (error) {
    console.error('Error fetching airport data:', error);
    return 0;
  }
};

// ─── Distance ────────────────────────────────────────────────────────────────

const toRadians = (degrees) => degrees * (Math.PI / 180);

export const calculateDistance = (lat1, lon1, lat2, lon2, unit = 'nm') => {
  const R = unit === 'km' ? 6371 : unit === 'sm' ? 3959 : 3440.065;

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10;
};

export const getAirportDistance = async (departureCode, arrivalCode) => {
  if (!departureCode || !arrivalCode || departureCode === arrivalCode) {
    return null;
  }

  try {
    const [depAirport, arrAirport] = await Promise.all([
      fetchAirport(departureCode),
      fetchAirport(arrivalCode)
    ]);

    if (!depAirport.latitude || !depAirport.longitude ||
        !arrAirport.latitude || !arrAirport.longitude) {
      console.warn('Missing coordinates for distance calculation');
      return null;
    }

    const distanceNM = calculateDistance(
      parseFloat(depAirport.latitude),
      parseFloat(depAirport.longitude),
      parseFloat(arrAirport.latitude),
      parseFloat(arrAirport.longitude),
      'nm'
    );

    return { distanceNM, departureAirport: depAirport, arrivalAirport: arrAirport };
  } catch (error) {
    console.error('Error calculating airport distance:', error);
    return null;
  }
};

// ─── Night time calculation (FAA civil twilight definition) ──────────────────
// Uses the NOAA solar equations (day-of-year method).
// Returns morningCT and eveningCT as raw UTC hours (may be <0 or >24 —
// callers must NOT normalize to 0-24 so cross-midnight comparisons work).
export const getCivilTwilightUTC = (dateStr, lat, lon) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const start = new Date(year, 0, 0);
  const diff = new Date(year, month - 1, day) - start;
  const doy = Math.round(diff / 86400000);
  // Fractional year (radians)
  const B = (2 * Math.PI / 365) * (doy - 1);
  // Equation of time (minutes)
  const eqtime = 229.18 * (0.000075 + 0.001868 * Math.cos(B) - 0.032077 * Math.sin(B)
    - 0.014615 * Math.cos(2 * B) - 0.04089 * Math.sin(2 * B));
  // Solar declination (radians)
  const decl = 0.006918 - 0.399912 * Math.cos(B) + 0.070257 * Math.sin(B)
    - 0.006758 * Math.cos(2 * B) + 0.000907 * Math.sin(2 * B)
    - 0.002697 * Math.cos(3 * B) + 0.00148 * Math.sin(3 * B);
  const latRad = lat * Math.PI / 180;
  // Civil twilight: sun 6° below horizon → zenith angle 96°
  const cosHa = (Math.cos(96 * Math.PI / 180) - Math.sin(latRad) * Math.sin(decl))
              / (Math.cos(latRad) * Math.cos(decl));
  if (cosHa < -1 || cosHa > 1) return null; // polar day/night
  const ha = Math.acos(cosHa) * 180 / Math.PI;
  // Solar noon in UTC minutes from midnight (lon West = negative → increases UTC noon)
  const solarNoonMin = 720 - 4 * lon - eqtime;
  return {
    morningCT: (solarNoonMin - 4 * ha) / 60,
    eveningCT: (solarNoonMin + 4 * ha) / 60
  };
};

const calculateNightMinutes = (flightStartUTC, flightEndUTC, depTwilight, arrTwilight) => {
  if (!depTwilight || !arrTwilight) return 0;
  const flightDuration = flightEndUTC - flightStartUTC;
  if (flightDuration <= 0) return 0;
  let nightMinutes = 0;
  const steps = Math.ceil(flightDuration);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const currentUTC = flightStartUTC + i; // raw UTC minutes, may be >1440
    // Interpolate civil twilight in raw UTC minutes (no normalization)
    const morningMin = (depTwilight.morningCT * (1 - t) + arrTwilight.morningCT * t) * 60;
    const eveningMin = (depTwilight.eveningCT * (1 - t) + arrTwilight.eveningCT * t) * 60;
    if (currentUTC < morningMin || currentUTC > eveningMin) nightMinutes++;
  }
  return nightMinutes;
};

// Local HHMM (string or number) → minutes after local midnight
const localHHMMToMinutes = (value) => {
  const str = value.toString().replace(/\D/g, '').padStart(4, '0').substring(0, 4);
  return parseInt(str.substring(0, 2)) * 60 + parseInt(str.substring(2));
};

export const calculateNightTime = async (depCode, arrCode, dateStr, actualOut, actualIn, depOffset, arrOffset) => {
  if (!depCode || !dateStr || !actualOut || !actualIn) return null;
  try {
    const depAirport = await fetchAirport(depCode);
    if (!depAirport.latitude || !depAirport.longitude) return null;
    let arrAirport = depAirport;
    if (arrCode && arrCode !== depCode) {
      const arrData = await fetchAirport(arrCode);
      if (arrData.latitude) arrAirport = arrData;
    }
    const depTwilight = getCivilTwilightUTC(dateStr, parseFloat(depAirport.latitude), parseFloat(depAirport.longitude));
    const arrTwilight = getCivilTwilightUTC(dateStr, parseFloat(arrAirport.latitude), parseFloat(arrAirport.longitude));
    const outLocalMin = localHHMMToMinutes(actualOut);
    const inLocalMin = localHHMMToMinutes(actualIn);
    if (isNaN(outLocalMin) || isNaN(inLocalMin)) return null;
    const outUTCMin = outLocalMin - depOffset * 60;
    let inUTCMin = inLocalMin - arrOffset * 60;
    if (inUTCMin <= outUTCMin) inUTCMin += 1440;
    return minutesToHours(calculateNightMinutes(outUTCMin, inUTCMin, depTwilight, arrTwilight));
  } catch (error) {
    console.error('Error calculating night time:', error);
    return null;
  }
};

// Is the takeoff (at actual_out) and landing (at actual_in) at night?
export const determineTakeoffLandingNight = async ({ depAirport, arrAirport, flightDate, outTime, inTime }) => {
  let takeoffIsNight = false;
  let landingIsNight = false;

  if (depAirport && flightDate && outTime) {
    try {
      const depOffset = await getAirportTimezone(depAirport, flightDate);
      const outUTCMin = localHHMMToMinutes(outTime) - depOffset * 60;
      const dep = await fetchAirport(depAirport);
      if (dep.latitude) {
        const twi = getCivilTwilightUTC(flightDate, parseFloat(dep.latitude), parseFloat(dep.longitude));
        if (twi) takeoffIsNight = outUTCMin < twi.morningCT * 60 || outUTCMin > twi.eveningCT * 60;
      }
    } catch (e) {
      console.error('Error determining takeoff day/night:', e);
    }
  }

  if (arrAirport && flightDate && inTime) {
    try {
      const depOffset = await getAirportTimezone(depAirport || arrAirport, flightDate);
      const arrOffset = await getAirportTimezone(arrAirport, flightDate);
      const outUTCMin = localHHMMToMinutes(outTime || '0000') - depOffset * 60;
      let inUTCMin = localHHMMToMinutes(inTime) - arrOffset * 60;
      if (inUTCMin <= outUTCMin) inUTCMin += 1440; // overnight flight
      const arr = await fetchAirport(arrAirport);
      if (arr.latitude) {
        const twi = getCivilTwilightUTC(flightDate, parseFloat(arr.latitude), parseFloat(arr.longitude));
        if (twi) landingIsNight = inUTCMin < twi.morningCT * 60 || inUTCMin > twi.eveningCT * 60;
      }
    } catch (e) {
      console.error('Error determining landing day/night:', e);
    }
  }

  return { takeoffIsNight, landingIsNight };
};

// ─── Aircraft classification ─────────────────────────────────────────────────

// Derive the time-category flags from an aircraft / aircraft_types row
export const classifyAircraft = (aircraft) => {
  const cls = (aircraft.class || '').toLowerCase();
  const engine = (aircraft.engine_type || '').toLowerCase();
  const isTurboFan = ['turbofan', 'turbo-fan', 'turbo fan', 'jet'].some(s => engine.includes(s));
  const isTurboProp = ['turboprop', 'turbo-prop', 'turbo prop'].some(s => engine.includes(s));
  return {
    isMultiEngine: cls.includes('multi'),
    isSingleEngineLand: cls.includes('single') && cls.includes('land'),
    // Jets, turbofans and turboprops are all turbine engines
    isTurbine: engine.includes('turbine') || isTurboFan || isTurboProp,
    isTurboFan,
    isTurboProp,
    class: aircraft.class,
    engine_type: aircraft.engine_type
  };
};

// Fill the aircraft-category time fields from total time
export const applyAircraftCategoryTimes = (setValue, info, totalTime) => {
  if (info.isMultiEngine) {
    setValue('amel_time', totalTime);
    setValue('asel_time', 0);
  } else {
    setValue('amel_time', 0);
    setValue('ames_time', 0);
    if (info.isSingleEngineLand) {
      setValue('asel_time', totalTime);
    }
  }
  setValue('turbine_time', info.isTurbine ? totalTime : 0);
  setValue('turbo_fan_time', info.isTurboFan ? totalTime : 0);
  setValue('turbo_prop_time', info.isTurboProp ? totalTime : 0);
};
